export function clampText(value, max) {
  return String(value ?? "").slice(0, max);
}

export function buildProperties({ name = "", date = "" } = {}) {
  const props = {};
  if (name) props["Name / Botschaft"] = name;
  if (date) props["Datum"] = date;
  return props;
}

export function selectVariant(variants, selection) {
  return variants.find(v =>
    v.options.length === selection.length &&
    v.options.every((opt, i) => opt === selection[i])
  ) ?? null;
}

export function formatEuro(cents) {
  return (cents / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}

export function buildPriceState(price, compareAt) {
  const current = Number(price) || 0;
  const compareValue = Number(compareAt) || 0;
  const onSale = compareValue > current;
  return {
    current: formatEuro(current),
    compare: onSale ? formatEuro(compareValue) : "",
    percent: onSale ? Math.round(((compareValue - current) * 100) / compareValue) : 0,
    onSale
  };
}

// Sichtbarkeit eines Galerie-Thumbnails für die gewählte Farbe.
// Leerer thumbColor = geteiltes Bild (bei jeder Farbe sichtbar).
export function matchesColor(thumbColor, selectedColor) {
  return thumbColor === "" || thumbColor === selectedColor;
}

export function visibleMedia(media, selectedColor) {
  const hasExactColor = media.some(item => item.color === selectedColor);
  return hasExactColor
    ? media.filter(item => matchesColor(item.color ?? "", selectedColor))
    : media;
}

export function activeSlideIndex(scrollLeft, slideWidth, count) {
  if (!slideWidth || count < 1) return 0;
  return Math.max(0, Math.min(count - 1, Math.round(scrollLeft / slideWidth)));
}

export function templateUrlForColor(color, templates, fallback = "") {
  return templates[color] || fallback;
}

function initProductConfigurator(root) {
  if (root.dataset.configuratorInitialized === "true") return;
  const variants = JSON.parse(root.querySelector("[data-variants]").textContent)
    .map(v => ({ id: v.id, options: v.options, available: v.available, price: v.price, compareAt: v.compare_at_price }));
  if (!variants.length) return;
  root.dataset.configuratorInitialized = "true";
  const idField = root.querySelector("[data-variant-id]");
  const addBtn = root.querySelector("[data-add-to-cart]");
  const nameInput = root.querySelector("[data-prop-name]");
  const previewText = root.querySelector("[data-preview-text]");
  const previewDate = root.querySelector("[data-preview-date]");
  const dateInput = root.querySelector("[data-prop-date]");
  const priceDisplay = root.querySelector("[data-price-display]");

  // Produktgalerie und mobile Personalisierungsvorlage bleiben bewusst getrennt.
  const galleryTrack = root.querySelector("[data-gallery-track]");
  const slides = Array.from(root.querySelectorAll("[data-gallery-slide]"));
  const dots = Array.from(root.querySelectorAll("[data-gallery-dot]"));
  const inlineImg = root.querySelector(".personalisierung-inline-preview__img");
  const thumbnails = Array.from(root.querySelectorAll("[data-thumbnail]"));
  const templates = {
    "Weiß": inlineImg?.dataset.templateWeiss ?? "",
    "Schwarz": inlineImg?.dataset.templateSchwarz ?? ""
  };
  const templateFallback = inlineImg?.dataset.templateFallback ?? "";
  let shownSlides = slides;
  let scrollFrame = 0;

  function setActiveMedia(mediaId) {
    slides.forEach(slide => {
      slide.classList.toggle("gallery-slide--active", slide.dataset.mediaId === mediaId);
    });
    dots.forEach(dot => {
      const active = dot.dataset.mediaId === mediaId;
      dot.setAttribute("aria-pressed", active ? "true" : "false");
      dot.classList.toggle("gallery-dot--active", active);
    });
    thumbnails.forEach(thumbnail => {
      const active = thumbnail.dataset.mediaId === mediaId;
      thumbnail.setAttribute("aria-pressed", active ? "true" : "false");
      thumbnail.classList.toggle("product-thumbnail--active", active);
    });
  }

  function scrollToMedia(mediaId, behavior = "smooth") {
    const slide = shownSlides.find(item => item.dataset.mediaId === mediaId);
    if (!slide || !galleryTrack) return;
    if (typeof galleryTrack.scrollTo === "function") {
      galleryTrack.scrollTo({ left: slide.offsetLeft, behavior });
    } else {
      galleryTrack.scrollLeft = slide.offsetLeft;
    }
    setActiveMedia(mediaId);
  }

  function handleGalleryControl(event) {
    scrollToMedia(event.currentTarget.dataset.mediaId);
  }

  function syncTemplate(color) {
    if (!inlineImg) return;
    const url = templateUrlForColor(color, templates, templateFallback);
    if (!url || inlineImg.src === new URL(url, document.baseURI).href) return;
    inlineImg.src = url;
    inlineImg.srcset = "";
  }

  function handleGalleryScroll() {
    if (!galleryTrack || scrollFrame) return;
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0;
      const index = activeSlideIndex(galleryTrack.scrollLeft, galleryTrack.clientWidth, shownSlides.length);
      const active = shownSlides[index];
      if (active) setActiveMedia(active.dataset.mediaId);
    });
  }

  thumbnails.forEach(btn => btn.addEventListener("click", handleGalleryControl));
  dots.forEach(btn => btn.addEventListener("click", handleGalleryControl));
  galleryTrack?.addEventListener("scroll", handleGalleryScroll, { passive: true });

  // Galerie auf die gewählte Farbe filtern; geteilte Bilder (data-color="") bleiben.
  function applyColorGallery(color) {
    syncTemplate(color);
    const visible = visibleMedia(
      slides.map(slide => ({ id: slide.dataset.mediaId, color: slide.dataset.color ?? "" })),
      color
    );
    const visibleIds = new Set(visible.map(item => item.id));
    slides.forEach(slide => { slide.hidden = !visibleIds.has(slide.dataset.mediaId); });
    dots.forEach(dot => { dot.hidden = !visibleIds.has(dot.dataset.mediaId); });
    thumbnails.forEach(thumbnail => { thumbnail.hidden = !visibleIds.has(thumbnail.dataset.mediaId); });
    shownSlides = slides.filter(slide => !slide.hidden);
    const first = shownSlides[0];
    if (first) scrollToMedia(first.dataset.mediaId, "auto");
  }

  // Optionsindizes dynamisch aus dem DOM (Phase 1: nur Farbe = 0; später mehr).
  const optionIndices = [...new Set(
    Array.from(root.querySelectorAll("[data-option-index]"))
      .map(el => Number(el.dataset.optionIndex))
  )].sort((a, b) => a - b);

  function currentSelection() {
    if (!optionIndices.length) return variants[0].options.slice();
    return optionIndices.map(i =>
      root.querySelector(`[data-option-index="${i}"]:checked`)?.value ?? variants[0].options[i]
    );
  }

  function syncPreview() {
    if (previewText && nameInput) previewText.textContent = clampText(nameInput.value, 24);
    if (previewDate && dateInput) previewDate.textContent = dateInput.value;
  }

  let lastColor = null;
  function sync() {
    if (nameInput) nameInput.value = clampText(nameInput.value, 24);
    syncPreview();
    const variant = selectVariant(variants, currentSelection());
    if (variant) {
      idField.value = variant.id;
      const priceState = buildPriceState(variant.price, variant.compareAt);
      const currentPrice = priceDisplay?.querySelector("[data-price-current]");
      const comparePrice = priceDisplay?.querySelector("[data-price-compare]");
      const priceBadge = priceDisplay?.querySelector("[data-price-badge]");
      if (currentPrice) currentPrice.textContent = priceState.current;
      if (comparePrice) {
        comparePrice.textContent = priceState.compare;
        comparePrice.hidden = !priceState.onSale;
      }
      if (priceBadge) {
        priceBadge.textContent = priceState.onSale ? `-${priceState.percent}%` : "";
        priceBadge.hidden = !priceState.onSale;
      }
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available
        ? `In den Warenkorb · ${formatEuro(variant.price)}`
        : "Ausverkauft";
    }
    // Galerie nur umschalten, wenn sich die Farbe wirklich ändert.
    const color = root.querySelector("[data-color-option]:checked")?.value ?? null;
    if (color !== null && color !== lastColor) {
      applyColorGallery(color);
      lastColor = color;
    }
  }
  root.addEventListener("input", sync);
  root.addEventListener("change", sync);
  sync();
  syncPreview();

  function cleanup() {
    root.removeEventListener("input", sync);
    root.removeEventListener("change", sync);
    thumbnails.forEach(btn => btn.removeEventListener("click", handleGalleryControl));
    dots.forEach(btn => btn.removeEventListener("click", handleGalleryControl));
    galleryTrack?.removeEventListener("scroll", handleGalleryScroll);
    if (scrollFrame) cancelAnimationFrame(scrollFrame);
    delete root.dataset.configuratorInitialized;
    document.removeEventListener("shopify:section:unload", handleSectionUnload);
  }

  function handleSectionUnload(event) {
    if (event.target === root || event.target?.contains?.(root)) cleanup();
  }

  document.addEventListener("shopify:section:unload", handleSectionUnload);
}

if (typeof document !== "undefined") {
  const initWithin = scope => {
    if (scope.matches?.("[data-product-configurator]")) initProductConfigurator(scope);
    scope.querySelectorAll?.("[data-product-configurator]").forEach(initProductConfigurator);
  };
  initWithin(document);
  document.addEventListener("shopify:section:load", event => initWithin(event.target));
}
